import React from 'react';
import { Container, Typography, Box, Paper, Alert } from '@mui/material';

const TakedownPage = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 3, md: 5 }, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          📄 Notice & Takedown Procedure
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: July 8, 2025
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          GrooveGate takes the protection of intellectual property seriously. This procedure describes how rights holders can report potential copyright infringements and how we handle them. It serves to comply with legal requirements (§ 10 TMG, § 8–10 DDG, possibly Art. 16 DSA) and to protect our users.
        </Alert>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              1. Reporting Alleged Rights Violations
            </Typography>
            <Typography paragraph>
              If you believe that content on GrooveGate violates your rights (e.g., copyright, trademark), please send us a formal notice via email to the following address:
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <Typography variant="body2">
                📧 <strong>legal@groovegate.com</strong>
              </Typography>
            </Box>
            <Typography paragraph>
              or
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <Typography variant="body2">
                📬 <strong>GrooveGate UG (haftungsbeschränkt)</strong><br />
                Attn: Legal Department<br />
                Warschauer Str. 59A<br />
                10243 Berlin, Germany
              </Typography>
            </Box>
            <Typography paragraph>
              Please include the following information in your notice:
            </Typography>
            <Typography component="ol" sx={{ pl: 3 }}>
              <li><strong>Identification of the violated right</strong><br />
                  (e.g., "I am the author of this musical piece", "I am the owner of trademark X")</li>
              <li><strong>Detailed description of the affected content</strong><br />
                  including URL or other unique identifying features on GrooveGate</li>
              <li><strong>Justification of the rights violation</strong><br />
                  (e.g., no license granted, publication without consent, etc.)</li>
              <li><strong>Your contact information</strong><br />
                  Name, address, email address, phone number if applicable</li>
              <li><strong>Proof of your authorization</strong><br />
                  (e.g., link to original, contracts, screenshots, etc.)</li>
              <li><strong>Sworn declaration (freely formulated):</strong><br />
                  "I certify that the above information is true and I am authorized to act on behalf of the rights holders."</li>
            </Typography>
            <Typography paragraph>
              <strong>Incomplete requests cannot be processed.</strong>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              2. Review and Response
            </Typography>
            <Typography paragraph>
              Upon receipt of the notice, GrooveGate will proceed as follows:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>We will review the reported content immediately and carefully.</li>
              <li>In case of obvious violations or sufficient suspicion, the affected content will be temporarily deactivated or removed.</li>
              <li>The affected user will be informed about the measure and the reason.</li>
              <li>GrooveGate offers – if legally required – the possibility for a statement (Counter-Notice).</li>
              <li>With credible proof of authorization or agreement between the parties, the content may be restored.</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              3. Abuse & Sanctions
            </Typography>
            <Typography paragraph>
              If this procedure is used abusively (e.g., with intentionally false information), we reserve the right to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>Initiate legal action</li>
              <li>Block accounts</li>
              <li>Claim damages</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              4. Transparency Report (DSA Relevance)
            </Typography>
            <Typography paragraph>
              Should GrooveGate become subject to the Digital Services Act (DSA) as a "hosting platform" in the future, we will publish a semi-annual transparency report on the number and type of complaints.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              5. Contact for Legal Questions
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2">
                📧 <strong>legal@groovegate.com</strong>
              </Typography>
            </Box>
            <Typography paragraph sx={{ mt: 2 }}>
              This address is exclusively for legal matters, particularly in connection with copyright, data protection, or platform content.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default TakedownPage;
